import React from 'react';
import { Box, Stack, ToggleButton, ToggleButtonGroup, Typography, SvgIcon, Tooltip } from '@mui/material';
import PrimaryIcon from 'shared-components/images/ro_portal/Primary.svg?react';
import MetastasisIcon from 'shared-components/images/ro_portal/Metastasis.svg?react';
import { DIAGNOSIS_STATUS, DiagnosisRow } from './useDiagnosisData';

interface DiagnosisTypeSelectionProps {
  type: 'primary' | 'metastasis' | null;
  onSelectionChange: (diagnosisType: 'primary' | 'metastasis') => void;
  isSubmitted: boolean;
  selectedPrimaryDiagnosis: DiagnosisRow | null;
}

const DiagnosisTypeSelection = (props: DiagnosisTypeSelectionProps): JSX.Element => {
  const { type, onSelectionChange, isSubmitted, selectedPrimaryDiagnosis } = props;

  const getTooltipText = () => {
    if (!isSubmitted) return '';
    if (type == 'primary') {
      if (selectedPrimaryDiagnosis?.status === DIAGNOSIS_STATUS.EXISTING) {
        return 'Once an existing primary diagnosis has been submitted, it cannot be changed';
      }
      return 'Once a primary diagnosis has been submitted, it cannot be changed to a metastasis diagnosis';
    }
    return 'Once a metastasis diagnosis has been submitted, it cannot be modified';
  };

  const primaryDisabled = isSubmitted && type === 'metastasis';
  const metastasisDisabled = isSubmitted && type === 'primary';

  return (
    <Stack
      direction="column"
      spacing={2}
      id="diagnosis"
      sx={{
        width: '100%',
      }}>
      <Typography variant="h5">What are you treating?</Typography>
      <Tooltip title={getTooltipText()} followCursor={true}>
        <ToggleButtonGroup
          sx={{ width: '100%' }}
          exclusive
          value={type}
          onChange={(_, value) => onSelectionChange(value)}
          aria-label="Diagnosis Type">
          <ToggleButton value="primary" color="secondary" sx={{ width: '267px' }} disabled={primaryDisabled}>
            <Stack direction="row" alignItems="center" gap={1}>
              <SvgIcon color={primaryDisabled ? 'disabled' : 'primary'} component={PrimaryIcon} />
              <Typography variant="button" color={primaryDisabled ? 'text.disabled' : 'text.primary'}>
                Primary Diagnosis
              </Typography>
            </Stack>
          </ToggleButton>
          <ToggleButton
            value="metastasis"
            color="secondary"
            sx={{ width: '267px' }}
            disabled={metastasisDisabled && type == 'primary'}>
            <Stack direction="row" alignItems="center" gap={1}>
              <SvgIcon color={metastasisDisabled ? 'disabled' : 'primary'} component={MetastasisIcon} />
              <Typography variant="button" color={metastasisDisabled ? 'text.disabled' : 'text.primary'}>
                Metastatic Diagnosis
              </Typography>
            </Stack>
          </ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
    </Stack>
  );
};

export default DiagnosisTypeSelection;
